import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    apiListProjects,
    apiListShifts,
    apiListAssignments,
    apiListLeaves,
    apiListUsers,
    apiAssignableUsers,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import {
    ChevronLeft,
    ChevronRight,
    CalendarDays,
    PhoneCall,
    Plane,
    Plus,
    AlertTriangle,
} from "lucide-react";
import AssignmentDialog from "@/components/roster/AssignmentDialog";
import CopyShiftsDialog from "@/components/roster/CopyShiftsDialog";
import { Copy } from "lucide-react";
import {
    toISO,
    fromISO,
    addDays,
    startOfWeek,
    startOfMonth,
    endOfMonth,
    formatShortDate,
    formatWeekday,
    formatLong,
    fmtTime,
    isSameDay,
    isBetweenISO,
} from "@/lib/date";

const MONTHS_LONG_ROSTER = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const CAN_EDIT_ROLES = new Set(["Super Admin", "Admin", "Project Manager", "Team Lead"]);
const CAN_COPY_ROLES = new Set(["Super Admin", "Admin", "Project Manager"]);

export default function Roster() {
    const { user } = useAuth();
    const isClient = user?.role === "Client";
    const canEdit = CAN_EDIT_ROLES.has(user?.role);
    const canCopy = CAN_COPY_ROLES.has(user?.role);

    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState("");
    const [view, setView] = useState("week"); // 'week' | 'month'
    const [anchor, setAnchor] = useState(new Date());
    const [shifts, setShifts] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [userDir, setUserDir] = useState({});
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // {date, shift, existing}
    const [copyOpen, setCopyOpen] = useState(false);

    const authorizedKey = (user?.authorized_projects || []).join(",");

    // Load projects
    useEffect(() => {
        (async () => {
            try {
                let list = await apiListProjects();
                // Client: only their authorized ones
                if (isClient) {
                    const allow = new Set(user?.authorized_projects || []);
                    list = list.filter((p) => allow.has(p.project_id));
                }
                setProjects(list);
                if (!projectId && list.length > 0) {
                    const firstActive = list.find((p) => p.active) || list[0];
                    setProjectId(firstActive.project_id);
                }
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClient, authorizedKey]);

    const range = useMemo(() => computeRange(view, anchor), [view, anchor]);

    // Load shifts + assignments + leaves + users for the current range
    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const [shiftsRes, assignmentsRes] = await Promise.all([
                apiListShifts(projectId),
                apiListAssignments({
                    project_id: projectId,
                    from_date: toISO(range.start),
                    to_date: toISO(range.end),
                }),
            ]);
            setShifts(shiftsRes);
            setAssignments(assignmentsRes);

            // Users directory (name/role). Choose the most privileged endpoint we can hit.
            let users = [];
            try {
                if (user?.role === "Super Admin" || user?.role === "Admin") {
                    users = await apiListUsers();
                } else if (user?.role === "Team Lead") {
                    users = await apiAssignableUsers(projectId);
                } else {
                    // Consultant / Client — can't list users. Best-effort: only show self.
                    users = [{ user_id: user.user_id, full_name: user.full_name, role: user.role }];
                }
            } catch {
                users = [];
            }
            const dir = {};
            users.forEach((u) => (dir[u.user_id] = u));
            setUserDir(dir);

            // Leaves — only for Team Lead+ (Consultant would see own; Client blocked)
            let lv = [];
            if (user?.role !== "Client" && user?.role !== "Consultant") {
                try {
                    lv = await apiListLeaves({
                        status: "Approved",
                        from_date: toISO(range.start),
                        to_date: toISO(range.end),
                    });
                } catch {
                    lv = [];
                }
            }
            setLeaves(lv);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [projectId, range.start, range.end, user]);

    useEffect(() => {
        load();
    }, [load]);

    // Group assignments by (date, shift_id)
    const grid = useMemo(() => {
        const m = {};
        for (const a of assignments) {
            const key = `${a.date}|${a.shift_id}`;
            (m[key] = m[key] || []).push(a);
        }
        return m;
    }, [assignments]);

    // Group approved leaves by user
    const leaveByUser = useMemo(() => {
        const m = {};
        for (const l of leaves) {
            (m[l.user_id] = m[l.user_id] || []).push(l);
        }
        return m;
    }, [leaves]);

    function isUserOnLeave(userId, iso) {
        const arr = leaveByUser[userId] || [];
        return arr.some((l) => isBetweenISO(iso, l.start_date, l.end_date));
    }

    function moveBy(step) {
        const next = new Date(anchor);
        if (view === "week") next.setDate(next.getDate() + step * 7);
        else next.setMonth(next.getMonth() + step);
        setAnchor(next);
    }

    function openCell(iso, shift) {
        const key = `${iso}|${shift.shift_id}`;
        const existing = grid[key] || [];
        setDialog({ date: iso, shift, existing });
    }

    const currentProject = projects.find((p) => p.project_id === projectId);

    const gapCount = useMemo(() => {
        // gaps in current visible range for the selected project
        if (!currentProject || shifts.length === 0) return 0;
        const days = view === "week" ? weekDays(range.start) : monthDays(anchor);
        let g = 0;
        for (const d of days) {
            const iso = toISO(d);
            for (const s of shifts) {
                if (!(grid[`${iso}|${s.shift_id}`] || []).length) g += 1;
            }
        }
        return g;
    }, [grid, shifts, currentProject, view, anchor, range.start]);

    return (
        <Layout active="roster">
            <PageHeader
                title="Roster & Scheduling"
                subtitle={
                    isClient
                        ? "Read-only view of your project's shift roster."
                        : "Assign consultants to shifts, mark on-call, and spot coverage gaps."
                }
                actions={
                    <>
                        <Select value={projectId} onValueChange={setProjectId}>
                            <SelectTrigger
                                className="h-9 w-[220px]"
                                data-testid="roster-project-trigger"
                            >
                                <SelectValue placeholder="Choose a project" />
                            </SelectTrigger>
                            <SelectContent>
                                {projects.map((p) => (
                                    <SelectItem
                                        key={p.project_id}
                                        value={p.project_id}
                                        data-testid={`roster-project-option-${p.project_id}`}
                                    >
                                        {p.name} · {p.code}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="inline-flex items-center rounded-md border border-ntt-border bg-white overflow-hidden">
                            <button
                                type="button"
                                data-testid="roster-view-week"
                                onClick={() => setView("week")}
                                className={`h-9 px-3 text-[12px] font-medium transition-colors ${view === "week" ? "bg-[#0072BC] text-white" : "text-[#2B2F3A] hover:bg-[#F4F6F9]"}`}
                            >
                                Week
                            </button>
                            <button
                                type="button"
                                data-testid="roster-view-month"
                                onClick={() => setView("month")}
                                className={`h-9 px-3 text-[12px] font-medium transition-colors ${view === "month" ? "bg-[#0072BC] text-white" : "text-[#2B2F3A] hover:bg-[#F4F6F9]"}`}
                            >
                                Month
                            </button>
                        </div>
                        {canCopy && (
                            <Button
                                variant="outline"
                                onClick={() => setCopyOpen(true)}
                                disabled={!projectId || shifts.length === 0}
                                data-testid="roster-copy-shifts-btn"
                                className="h-9 text-[12px]"
                                title="Copy this week's shift assignments to future weeks"
                            >
                                <Copy className="h-3.5 w-3.5 mr-1.5" />
                                Copy shifts
                            </Button>
                        )}
                    </>
                }
            />

            {/* Range nav */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <Button
                    variant="outline"
                    onClick={() => moveBy(-1)}
                    data-testid="roster-prev"
                    className="h-9"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setAnchor(new Date())}
                    data-testid="roster-today"
                    className="h-9 text-[12px]"
                >
                    Today
                </Button>
                <Button
                    variant="outline"
                    onClick={() => moveBy(1)}
                    data-testid="roster-next"
                    className="h-9"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
                <div
                    className="text-[13px] font-semibold text-[#070F26] ml-2 flex items-center gap-2"
                    data-testid="roster-range-label"
                >
                    <CalendarDays className="h-4 w-4 text-ntt-muted" />
                    {view === "week"
                        ? `${formatShortDate(range.start)} to ${formatShortDate(range.end)}`
                        : `${MONTHS_LONG_ROSTER[anchor.getMonth()]} ${anchor.getFullYear()}`}
                </div>
                <div className="ml-auto flex items-center gap-2 text-[12px]">
                    <Legend />
                    {gapCount > 0 && (
                        <span
                            data-testid="gap-count"
                            className="inline-flex items-center gap-1 h-6 px-2 rounded text-[11px] font-medium bg-red-50 text-red-700 border border-red-200"
                        >
                            <AlertTriangle className="h-3 w-3" />
                            {gapCount} Gap{gapCount === 1 ? "" : "s"}
                        </span>
                    )}
                </div>
            </div>

            {!projectId ? (
                <div className="bg-white border border-ntt-border rounded-xl p-10 text-center text-sm text-ntt-muted">
                    {isClient
                        ? "You are not authorised on any project yet."
                        : "Add a project first, then define its shifts."}
                </div>
            ) : loading ? (
                <div className="bg-white border border-ntt-border rounded-xl p-10 text-center text-sm text-ntt-muted">
                    Loading roster…
                </div>
            ) : shifts.length === 0 ? (
                <div className="bg-white border border-ntt-border rounded-xl p-10 text-center text-sm text-ntt-muted">
                    No shifts defined for this project yet.
                </div>
            ) : view === "week" ? (
                <WeekView
                    start={range.start}
                    shifts={shifts}
                    grid={grid}
                    userDir={userDir}
                    isUserOnLeave={isUserOnLeave}
                    openCell={openCell}
                    canEdit={canEdit}
                />
            ) : (
                <MonthView
                    anchor={anchor}
                    shifts={shifts}
                    grid={grid}
                    openCell={openCell}
                    userDir={userDir}
                />
            )}

            {/* On-leave panel */}
            {leaves.length > 0 && (
                <div
                    className="mt-6 bg-white border border-ntt-border rounded-xl p-4"
                    data-testid="on-leave-panel"
                >
                    <div className="text-[11px] uppercase tracking-wider text-ntt-muted mb-2 flex items-center gap-1">
                        <Plane className="h-3.5 w-3.5" />
                        On approved leave in view
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {leaves.map((l) => (
                            <span
                                key={l.leave_id}
                                data-testid={`leave-chip-${l.leave_id}`}
                                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] bg-[#DBEAF6] text-[#0072BC]"
                            >
                                {userDir[l.user_id]?.full_name || l.user_id}
                                <span className="text-ntt-muted">
                                    · {l.start_date} → {l.end_date}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {dialog && (
                <AssignmentDialog
                    open={!!dialog}
                    onOpenChange={(v) => !v && setDialog(null)}
                    date={dialog.date}
                    shift={dialog.shift}
                    project={currentProject}
                    existing={dialog.existing}
                    userDirectory={userDir}
                    canEdit={canEdit}
                    onChanged={() => {
                        load();
                        // refresh dialog existing
                        setDialog((d) => {
                            if (!d) return d;
                            return { ...d };
                        });
                    }}
                />
            )}
            {canCopy && (
                <CopyShiftsDialog
                    open={copyOpen}
                    onOpenChange={setCopyOpen}
                    project={currentProject}
                    sourceWeekStart={range.start}
                    sourceWeekEnd={range.end}
                    shifts={shifts}
                    sourceAssignments={assignments}
                    onSuccess={load}
                />
            )}
        </Layout>
    );
}
function WeekView({ start, shifts, grid, userDir, isUserOnLeave, openCell, canEdit }) {
    const days = weekDays(start);
    return (
        <div
            className="bg-white border border-ntt-border rounded-xl overflow-hidden"
            data-testid="week-view"
        >
            {/* Desktop grid */}
            <div className="hidden md:block">
                <div className="grid grid-cols-[160px_repeat(7,minmax(0,1fr))] bg-[#F4F6F9] border-b border-ntt-border">
                    <div className="p-3 text-[11px] uppercase tracking-wider text-ntt-muted">
                        Shift
                    </div>
                    {days.map((d) => {
                        const today = isSameDay(d, new Date());
                        return (
                            <div
                                key={toISO(d)}
                                className={`p-3 text-[12px] font-medium ${today ? "text-[#0072BC]" : "text-[#070F26]"}`}
                            >
                                <div className="uppercase text-[10px] tracking-wider text-ntt-muted">
                                    {formatWeekday(d)}
                                </div>
                                <div>{formatShortDate(d)}</div>
                            </div>
                        );
                    })}
                </div>
                {shifts.map((s) => (
                    <div
                        key={s.shift_id}
                        className="grid grid-cols-[160px_repeat(7,minmax(0,1fr))] border-b border-ntt-border last:border-0"
                    >
                        <div className="p-3 bg-[#FBFCFE]">
                            <div className="text-[13px] font-medium text-[#070F26]">
                                {s.name}
                            </div>
                            <div className="text-[11px] text-ntt-muted font-mono">
                                {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                            </div>
                        </div>
                        {days.map((d) => {
                            const iso = toISO(d);
                            const list = grid[`${iso}|${s.shift_id}`] || [];
                            return (
                                <Cell
                                    key={iso}
                                    iso={iso}
                                    shift={s}
                                    list={list}
                                    userDir={userDir}
                                    isUserOnLeave={isUserOnLeave}
                                    onOpen={() => openCell(iso, s)}
                                    canEdit={canEdit}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Mobile: stacked days */}
            <div className="md:hidden divide-y divide-ntt-border">
                {days.map((d) => (
                    <div key={toISO(d)} className="p-3">
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="text-[13px] font-semibold text-[#070F26]">
                                {formatWeekday(d)}, {formatShortDate(d)}
                            </div>
                            {isSameDay(d, new Date()) && (
                                <span className="text-[10px] font-medium text-[#0072BC] uppercase">
                                    Today
                                </span>
                            )}
                        </div>
                        <div className="space-y-2">
                            {shifts.map((s) => {
                                const iso = toISO(d);
                                const list = grid[`${iso}|${s.shift_id}`] || [];
                                return (
                                    <MobileRow
                                        key={s.shift_id}
                                        iso={iso}
                                        shift={s}
                                        list={list}
                                        userDir={userDir}
                                        onOpen={() => openCell(iso, s)}
                                        canEdit={canEdit}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Cell({ iso, shift, list, userDir, isUserOnLeave, onOpen, canEdit }) {
    const empty = list.length === 0;
    return (
        <button
            type="button"
            onClick={onOpen}
            disabled={!canEdit && empty}
            data-testid={`cell-${iso}-${shift.shift_id}`}
            className={`text-left p-2 min-h-[76px] border-l border-ntt-border transition-colors ${
                empty
                    ? "bg-[#FEF6F6] hover:bg-[#FCECEC]"
                    : "bg-white hover:bg-[#F4F6F9]"
            } ${!canEdit ? "cursor-default" : ""}`}
        >
            {empty ? (
                <span
                    data-testid={`gap-${iso}-${shift.shift_id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700"
                >
                    <AlertTriangle className="h-3 w-3" />
                    Gap
                    {canEdit && (
                        <span className="text-red-500/70 ml-0.5">· add</span>
                    )}
                </span>
            ) : (
                <div className="space-y-1">
                    {list.slice(0, 3).map((a) => {
                        if (a.nil) {
                            return (
                                <div
                                    key={a.assignment_id}
                                    className="flex items-center gap-1.5"
                                    data-testid={`nil-${a.assignment_id}`}
                                >
                                    <span className="inline-flex items-center h-5 px-1.5 rounded bg-[#F4F6F9] text-[11px] font-medium text-ntt-muted border border-ntt-border">
                                        NIL
                                    </span>
                                    <span className="text-[11px] text-ntt-muted italic truncate">
                                        No assignment needed
                                    </span>
                                </div>
                            );
                        }
                        const u = userDir[a.user_id];
                        const displayName =
                            u?.full_name || a.user_full_name || "Unknown";
                        const onLeave = isUserOnLeave(a.user_id, iso);
                        return (
                            <div
                                key={a.assignment_id}
                                className="flex items-center gap-1.5"
                            >
                                <span
                                    title={displayName}
                                    className="text-[12px] text-[#070F26] truncate flex-1"
                                >
                                    {displayName}
                                </span>
                                {a.on_call && (
                                    <PhoneCall
                                        className="h-3 w-3 text-[#8a5a00]"
                                        aria-label="on call"
                                    />
                                )}
                                {onLeave && (
                                    <Plane
                                        className="h-3 w-3 text-red-500"
                                        aria-label="on leave"
                                    />
                                )}
                            </div>
                        );
                    })}
                    {list.length > 3 && (
                        <div className="text-[11px] text-ntt-muted">
                            +{list.length - 3} more
                        </div>
                    )}
                </div>
            )}
        </button>
    );
}

function MobileRow({ iso, shift, list, userDir, onOpen, canEdit }) {
    const empty = list.length === 0;
    return (
        <button
            type="button"
            onClick={onOpen}
            disabled={!canEdit && empty}
            data-testid={`m-cell-${iso}-${shift.shift_id}`}
            className={`w-full flex items-start gap-3 text-left border rounded-md p-2 ${
                empty
                    ? "border-red-200 bg-[#FEF6F6]"
                    : "border-ntt-border bg-white"
            }`}
        >
            <div className="w-20 shrink-0">
                <div className="text-[12px] font-medium text-[#070F26]">
                    {shift.name}
                </div>
                <div className="text-[10px] text-ntt-muted font-mono">
                    {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}
                </div>
            </div>
            <div className="flex-1 min-w-0">
                {empty ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700">
                        <AlertTriangle className="h-3 w-3" /> Gap
                    </span>
                ) : (
                    <div className="flex flex-wrap gap-1">
                        {list.map((a) => {
                            const u = userDir[a.user_id];
                            const name =
                                u?.full_name || a.user_full_name || "Unknown";
                            return (
                                <span
                                    key={a.assignment_id}
                                    className="inline-flex items-center gap-1 h-6 px-2 rounded bg-[#DBEAF6] text-[#0072BC] text-[11px]"
                                >
                                    {name}
                                    {a.on_call && (
                                        <PhoneCall className="h-3 w-3" />
                                    )}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>
            {canEdit && (
                <Plus className="h-4 w-4 text-ntt-muted mt-0.5" />
            )}
        </button>
    );
}

/* ---------------------- Month view ---------------------- */
function MonthView({ anchor, shifts, grid, openCell, userDir }) {
    const days = monthDays(anchor);
    const monthIdx = anchor.getMonth();
    return (
        <div className="bg-white border border-ntt-border rounded-xl overflow-hidden" data-testid="month-view">
            <div className="grid grid-cols-7 border-b border-ntt-border bg-[#F4F6F9]">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                    <div key={d} className="p-2 text-[11px] uppercase tracking-wider text-ntt-muted text-center">
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {days.map((d) => {
                    const iso = toISO(d);
                    const inMonth = d.getMonth() === monthIdx;
                    const today = isSameDay(d, new Date());
                    // per day: for each shift compute gap/assigned
                    const rows = shifts.map((s) => ({
                        shift: s,
                        list: grid[`${iso}|${s.shift_id}`] || [],
                    }));
                    return (
                        <div
                            key={iso}
                            data-testid={`m-day-${iso}`}
                            className={`min-h-[104px] border-b border-r border-ntt-border p-1.5 ${
                                inMonth ? "bg-white" : "bg-[#FBFCFE]"
                            }`}
                        >
                            <div
                                className={`text-[11px] font-medium mb-1 ${
                                    today
                                        ? "text-[#0072BC]"
                                        : inMonth
                                          ? "text-[#070F26]"
                                          : "text-ntt-muted"
                                }`}
                            >
                                {d.getDate()}
                            </div>
                            <div className="space-y-0.5">
                                {rows.slice(0, 3).map(({ shift, list }) => {
                                    const empty = list.length === 0;
                                    return (
                                        <button
                                            key={shift.shift_id}
                                            type="button"
                                            onClick={() => openCell(iso, shift)}
                                            data-testid={`m-chip-${iso}-${shift.shift_id}`}
                                            className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] truncate ${
                                                empty
                                                    ? "bg-[#FEF6F6] text-red-700 border border-red-200"
                                                    : "bg-[#DBEAF6] text-[#0072BC]"
                                            }`}
                                            title={
                                                empty
                                                    ? `${shift.name} · Gap`
                                                    : list
                                                          .map(
                                                              (a) =>
                                                                  userDir[a.user_id]
                                                                      ?.full_name ||
                                                                  a.user_full_name ||
                                                                  "?",
                                                          )
                                                          .join(", ")
                                            }
                                        >
                                            {empty
                                                ? `${shift.name} · Gap`
                                                : `${shift.name} · ${list.length}`}
                                        </button>
                                    );
                                })}
                                {rows.length > 3 && (
                                    <div className="text-[10px] text-ntt-muted pl-1">
                                        +{rows.length - 3} more
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Legend() {
    return (
        <div className="hidden md:flex items-center gap-2 text-[11px] text-ntt-muted">
            <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-sm bg-[#0072BC]" /> Assigned
            </span>
            <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-sm bg-red-400" /> Gap
            </span>
            <span className="inline-flex items-center gap-1">
                <PhoneCall className="h-3 w-3 text-[#8a5a00]" /> On Call
            </span>
        </div>
    );
}

/* ---------------------- Helpers ---------------------- */
function computeRange(view, anchor) {
    if (view === "week") {
        const s = startOfWeek(anchor);
        return { start: s, end: addDays(s, 6) };
    }
    return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
}

function weekDays(start) {
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

function monthDays(anchor) {
    const first = startOfMonth(anchor);
    const gridStart = startOfWeek(first);
    // 6 rows × 7 = 42 cells (covers all month layouts)
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}
